import * as THREE from 'three';
import useEditorStore from '../stores/useEditorStore';
import { logScaleIfChanged, logLockingSocketScale } from './lockingSocketScaleDebug';

/** Flexion: step-2 condyle frame local +X. */
const _localFlexionAxis = new THREE.Vector3(1, 0, 0);
/** Abduction: step-2 frame local +Z (origin → mid-patellar forward). */
const _localAbductionAxis = new THREE.Vector3(0, 0, 1);

const _qBase = new THREE.Quaternion();
const _composed = new THREE.Quaternion();
const _angleOnly = new THREE.Quaternion();
const _axis = new THREE.Vector3();
const _rotQuat = new THREE.Quaternion();

let _qBaseMeshUuid = null;
let _qBaseReady = false;
/** Skip gizmo merge while sliders set mesh.quaternion. */
let _applyingSocketAngles = false;

function getSocketAngleDeg() {
  const st = useEditorStore.getState();
  return {
    flexDeg: Number(st.alignmentSocketRedRingXDeg) || 0,
    abdDeg: Number(st.alignmentSocketAbductionZDeg) || 0,
  };
}

function normalizeQ(q) {
  if (q.lengthSq() < 1e-12) return q.identity();
  return q.normalize();
}

function composedMatchError(meshQuat, flexDeg, abdDeg) {
  composeAlignmentSocketAnglesQuaternion(_qBase, flexDeg, abdDeg, _composed);
  return 1 - Math.abs(meshQuat.dot(_composed));
}

function persistQBaseToStore() {
  const arr = _qBase.toArray();
  const st = useEditorStore.getState();
  if (typeof st.setAlignmentSocketQBase === 'function') {
    st.setAlignmentSocketQBase(arr);
  }
}

function rotationFromAxisDeg(axis, deg, out = _rotQuat) {
  if (Math.abs(deg) < 1e-10) return out.identity();
  if (axis.lengthSq() < 1e-12) return out.identity();
  axis.normalize();
  return out.setFromAxisAngle(axis, THREE.MathUtils.degToRad(deg));
}

function axisFromLocalInQuaternion(q, localAxis, out = _axis) {
  out.copy(localAxis).applyQuaternion(q);
  if (out.lengthSq() < 1e-12) return out.copy(localAxis);
  return out.normalize();
}

/**
 * Socket orientation = abduction (local +Z) then flexion (local +X), on top of gizmo qBase.
 * mesh.quaternion = flex * abd * qBase
 */
export function composeAlignmentSocketAnglesQuaternion(qBase, flexDeg, abdDeg, out = _composed) {
  out.copy(qBase);
  if (Math.abs(abdDeg) > 1e-10) {
    axisFromLocalInQuaternion(out, _localAbductionAxis);
    rotationFromAxisDeg(_axis, abdDeg);
    out.premultiply(_rotQuat);
  }
  if (Math.abs(flexDeg) > 1e-10) {
    axisFromLocalInQuaternion(out, _localFlexionAxis);
    rotationFromAxisDeg(_axis, flexDeg);
    out.premultiply(_rotQuat);
  }
  return out;
}

function resetQBaseTracking(mesh) {
  if (!mesh || _qBaseMeshUuid === mesh.uuid) return;
  _qBaseMeshUuid = mesh.uuid;
  _qBase.identity();
  _qBaseReady = false;
}

/**
 * Exact inverse of compose: qBase = meshQuat * invert(flex * abd).
 * Replaces the old iterative extract that could diverge and freeze sliders.
 */
function solveQBaseFromMesh(mesh, flexDeg, abdDeg) {
  composeAlignmentSocketAnglesQuaternion(_angleOnly.identity(), flexDeg, abdDeg, _angleOnly);
  _qBase.copy(mesh.quaternion).multiply(_angleOnly.invert());
  normalizeQ(_qBase);
}

function loadStoredQBaseIntoModule() {
  const stored = useEditorStore.getState().alignmentSocketQBase;
  if (!Array.isArray(stored) || stored.length !== 4) return false;
  _qBase.fromArray(stored);
  normalizeQ(_qBase);
  return true;
}

/** Initialize qBase once per mesh (or after gizmo commit). Not called on every slider tick. */
function ensureQBaseForMesh(mesh, flexDeg, abdDeg) {
  if (!mesh) return;
  resetQBaseTracking(mesh);

  if (_qBaseReady) return;

  if (loadStoredQBaseIntoModule()) {
    _qBaseReady = true;
    if (composedMatchError(mesh.quaternion, flexDeg, abdDeg) < 1e-2) return;
  }

  if (Math.abs(flexDeg) < 1e-10 && Math.abs(abdDeg) < 1e-10) {
    _qBase.copy(mesh.quaternion);
    normalizeQ(_qBase);
  } else {
    solveQBaseFromMesh(mesh, flexDeg, abdDeg);
  }
  _qBaseReady = true;
  persistQBaseToStore();
}

/** Open socket align / new mesh: load or solve qBase once (never re-extract on slider moves). */
export function initializeAlignmentSocketQBase(mesh) {
  if (!mesh) return;
  const { flexDeg, abdDeg } = getSocketAngleDeg();
  ensureQBaseForMesh(mesh, flexDeg, abdDeg);
}

export function getAlignmentSocketQBaseArray() {
  return _qBase.toArray();
}

export function setAlignmentSocketQBaseFromArray(arr) {
  if (Array.isArray(arr) && arr.length === 4) {
    _qBase.fromArray(arr);
    normalizeQ(_qBase);
    _qBaseReady = true;
    persistQBaseToStore();
  }
}

export function applyAlignmentSocketAnglesToMesh(flexDeg, abdDeg) {
  const mesh = useEditorStore.getState().sceneRefs?.shadedMesh;
  if (!mesh) return;

  const angles = getSocketAngleDeg();
  const flex = flexDeg ?? angles.flexDeg;
  const abd = abdDeg ?? angles.abdDeg;

  const scaleBefore = mesh.scale.clone();

  resetQBaseTracking(mesh);
  ensureQBaseForMesh(mesh, flex, abd);

  _applyingSocketAngles = true;
  try {
    composeAlignmentSocketAnglesQuaternion(_qBase, flex, abd, _composed);
    if (!Number.isFinite(_composed.x + _composed.y + _composed.z + _composed.w)) {
      console.warn('[socketAlignRedRing] invalid composed quaternion — resetting qBase');
      _qBaseReady = false;
      ensureQBaseForMesh(mesh, flex, abd);
      composeAlignmentSocketAnglesQuaternion(_qBase, flex, abd, _composed);
    }
    const sx = mesh.scale.x;
    const sy = mesh.scale.y;
    const sz = mesh.scale.z;
    mesh.quaternion.copy(_composed);
    normalizeQ(mesh.quaternion);
    mesh.scale.set(sx, sy, sz);
    mesh.updateMatrixWorld(true);
  } finally {
    _applyingSocketAngles = false;
  }

  logScaleIfChanged('applyAlignmentSocketAnglesToMesh', mesh, {
    x: scaleBefore.x,
    y: scaleBefore.y,
    z: scaleBefore.z,
  }, { flexDeg: flex, abdDeg: abd, qBase: _qBase.toArray() });
}

/**
 * After rotate/move gizmo drag ends: solve qBase from mesh, then snap to sliders.
 */
export function commitAlignmentSocketGizmoRotation() {
  if (_applyingSocketAngles) return;

  const mesh = useEditorStore.getState().sceneRefs?.shadedMesh;
  if (!mesh) return;

  const scaleBefore = mesh.scale.clone();
  logLockingSocketScale('commitAlignmentSocketGizmoRotation:start', {
    scale: { x: scaleBefore.x, y: scaleBefore.y, z: scaleBefore.z },
    quaternion: mesh.quaternion.toArray(),
  });

  const { flexDeg, abdDeg } = getSocketAngleDeg();
  resetQBaseTracking(mesh);
  solveQBaseFromMesh(mesh, flexDeg, abdDeg);
  _qBaseReady = true;
  persistQBaseToStore();

  _applyingSocketAngles = true;
  try {
    composeAlignmentSocketAnglesQuaternion(_qBase, flexDeg, abdDeg, _composed);
    const sx = mesh.scale.x;
    const sy = mesh.scale.y;
    const sz = mesh.scale.z;
    mesh.quaternion.copy(_composed);
    normalizeQ(mesh.quaternion);
    mesh.scale.set(sx, sy, sz);
    mesh.updateMatrixWorld(true);
  } finally {
    _applyingSocketAngles = false;
  }

  logScaleIfChanged('commitAlignmentSocketGizmoRotation:end', mesh, {
    x: scaleBefore.x,
    y: scaleBefore.y,
    z: scaleBefore.z,
  }, { flexDeg, abdDeg });
}

/** @deprecated Gizmo commit only — do not call on attach or TC attach. */
export function mergeAlignmentSocketGizmoIntoQBase() {
  commitAlignmentSocketGizmoRotation();
}

/**
 * After locking attachment apply: rotation is baked into vertices; sync qBase only.
 * Do not use commitAlignmentSocketGizmoRotation (it re-snaps quaternion from sliders).
 */
export function syncQBaseAfterLockingAttachment(mesh) {
  if (!mesh) return;
  resetQBaseTracking(mesh);
  _qBase.copy(mesh.quaternion);
  normalizeQ(_qBase);
  _qBaseReady = true;
  persistQBaseToStore();
}

export function rederiveAlignmentSocketQBaseFromMesh() {
  const mesh = useEditorStore.getState().sceneRefs?.shadedMesh;
  if (!mesh) return;
  resetQBaseTracking(mesh);
  const { flexDeg, abdDeg } = getSocketAngleDeg();
  solveQBaseFromMesh(mesh, flexDeg, abdDeg);
  _qBaseReady = true;
  persistQBaseToStore();
}

export function applyAlignmentSocketAngles(flexDeg, abdDeg) {
  applyAlignmentSocketAnglesToMesh(flexDeg, abdDeg);
}

export function syncAlignmentSocketRedRingQBaseFromMesh() {
  commitAlignmentSocketGizmoRotation();
}

export function applyAlignmentSocketRedRingDegToMesh(deg) {
  applyAlignmentSocketAnglesToMesh(deg, undefined);
}

export function applyAlignmentSocketRedRingDeg(deg) {
  applyAlignmentSocketAnglesToMesh(deg, undefined);
}
